import { fixed, trim } from '../../math/format';
import {
  blockVolume,
  circleArea,
  cylinderVolume,
  DENSITIES,
  rectangleArea,
  weight,
  type Material,
} from '../../math/material';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

const MATERIALS = Object.keys(DENSITIES) as Material[];
const COMMON: readonly Material[] = ['mild steel', 'aluminium', 'brass', 'stainless steel'];

export const STOCK_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'stock.circleArea',
    topicId: 'stock',
    skill: 'Area of a round',
    gen: (rng) => {
      const diameter = rng.step(0.5, 6, 0.25);
      const solved = circleArea(diameter);
      const radius = diameter / 2;

      return {
        kind: 'mc',
        prompt: `What is the end area of a ${fixed(diameter, 3)}" round bar?`,
        formula: 'area = π × r²',
        why: `r is ${fixed(radius, 4)}", so π × ${fixed(radius * radius, 4)} = ${fixed(solved.value, 4)} square inches.`,
        steps: solved.steps,
        ...mcOptions(rng, `${fixed(solved.value, 3)} sq in`, [
          {
            label: `${fixed(Math.PI * diameter * diameter, 3)} sq in`,
            value: Math.PI * diameter * diameter,
            trap: 'You squared the diameter instead of the radius. Halve it first — that mistake makes the area four times too big.',
          },
          {
            label: `${fixed(Math.PI * diameter, 3)} sq in`,
            value: Math.PI * diameter,
            trap: 'That is the circumference, the distance around the outside. Area needs the radius squared.',
          },
          {
            label: `${fixed(radius * radius, 3)} sq in`,
            value: radius * radius,
            trap: 'You squared the radius but left out π.',
          },
        ]),
      };
    },
  },
  {
    id: 'stock.rectArea',
    topicId: 'stock',
    skill: 'Area of a rectangle',
    gen: (rng) => {
      const width = rng.step(0.5, 4, 0.25);
      const height = rng.step(0.5, 4, 0.25);
      const solved = rectangleArea(width, height);

      return {
        kind: 'numeric',
        prompt: `A ${fixed(width, 3)}" by ${fixed(height, 3)}" bar. What is the end area?`,
        sub: 'Square inches, three places.',
        answer: solved.value,
        tolerance: 0.001,
        unit: ' sq in',
        places: 3,
        formula: 'area = width × height',
        why: `${trim(width, 3)} × ${trim(height, 3)} = ${fixed(solved.value, 4)} square inches.`,
        steps: solved.steps,
      };
    },
  },
  {
    id: 'stock.cylinderVolume',
    topicId: 'stock',
    skill: 'Volume of a round bar',
    gen: (rng) => {
      const diameter = rng.step(0.5, 4, 0.25);
      const length = rng.step(2, 24, 1);
      const solved = cylinderVolume(diameter, length);

      return {
        kind: 'mc',
        prompt: `A ${fixed(diameter, 3)}" round, ${trim(length, 0)}" long. What volume?`,
        formula: 'volume = π × r² × length',
        why: `End area is ${fixed(circleArea(diameter).value, 4)} sq in, times ${trim(length, 0)}" of length = ${fixed(solved.value, 3)} cubic inches.`,
        steps: solved.steps,
        ...mcOptions(rng, `${fixed(solved.value, 2)} cu in`, [
          {
            label: `${fixed(Math.PI * diameter * diameter * length, 2)} cu in`,
            value: Math.PI * diameter * diameter * length,
            trap: 'Diameter squared instead of radius squared — four times too big.',
          },
          {
            label: `${fixed(circleArea(diameter).value, 2)} cu in`,
            value: circleArea(diameter).value,
            trap: 'That is the end area only. You still have to run it down the length.',
          },
          {
            label: `${fixed(diameter * length, 2)} cu in`,
            value: diameter * length,
            trap: 'No π and no squaring. A round bar is not a flat rectangle.',
          },
        ]),
      };
    },
  },
  {
    id: 'stock.weight',
    topicId: 'stock',
    skill: 'Weight of a piece of stock',
    gen: (rng) => {
      const material = rng.pick(COMMON);
      const width = rng.step(0.5, 3, 0.5);
      const height = rng.step(0.5, 3, 0.5);
      const length = rng.pick([6, 12, 18, 24]);
      const volume = blockVolume(width, height, length);
      const solved = weight(volume.value, material);

      /*
       * Contrast materials have to differ by enough that the weights are
       * visibly different once rounded. Mild steel and stainless are only 2%
       * apart, so at one decimal place they can print the same number and one
       * option silently disappears.
       */
      const density = DENSITIES[material];
      const contrasting = MATERIALS.filter(
        (m) => Math.abs(DENSITIES[m] - density) / density > 0.2,
      ).sort((a, b) => DENSITIES[a] - DENSITIES[b]);
      const lighter = contrasting[0]!;
      const heavier = contrasting[contrasting.length - 1]!;

      return {
        kind: 'mc',
        prompt: `A ${trim(width, 2)}" × ${trim(height, 2)}" × ${length}" piece of ${material}. Roughly what does it weigh?`,
        formula: 'weight = volume × density',
        why: `Volume is ${fixed(volume.value, 2)} cubic inches, and ${material} runs ${DENSITIES[material]} lb per cubic inch. ${fixed(volume.value, 2)} × ${DENSITIES[material]} = ${fixed(solved.value, 2)} lb.`,
        steps: [...volume.steps, ...solved.steps],
        ...mcOptions(rng, `${fixed(solved.value, 1)} lb`, [
          {
            label: `${fixed(volume.value * DENSITIES[heavier], 1)} lb`,
            value: volume.value * DENSITIES[heavier],
            trap: `That is the weight if it were ${heavier}. Density is per material, not one number for everything.`,
          },
          {
            label: `${fixed(volume.value * DENSITIES[lighter], 1)} lb`,
            value: volume.value * DENSITIES[lighter],
            trap: `That is the weight if it were ${lighter}.`,
          },
          {
            label: `${fixed(volume.value, 1)} lb`,
            value: volume.value,
            trap: 'That is the volume in cubic inches, not a weight. You still have to multiply by density.',
          },
        ]),
      };
    },
  },
  {
    id: 'stock.removed',
    topicId: 'stock',
    skill: 'Material removed from a bar',
    gen: (rng) => {
      const start = rng.step(1.5, 4, 0.25);
      const cut = rng.pick([0.25, 0.5, 0.75, 1.0]);
      const finish = Math.round((start - cut) * 10000) / 10000;
      const length = rng.pick([2, 4, 6, 12]);

      const before = cylinderVolume(start, length).value;
      const after = cylinderVolume(finish, length).value;
      const removed = before - after;

      return {
        kind: 'mc',
        prompt: `Turning a ${fixed(start, 3)}" bar down to ${fixed(finish, 3)}" over ${length}". How much material comes off?`,
        formula: 'removed = starting volume − finished volume',
        why: `${fixed(before, 3)} − ${fixed(after, 3)} = ${fixed(removed, 3)} cubic inches. Work out both volumes and subtract — the difference in diameter alone tells you nothing about volume.`,
        steps: [
          { kind: 'formula', text: 'removed = starting volume − finished volume' },
          { kind: 'substitute', text: `Start: π × (${fixed(start / 2, 4)})² × ${length}. Finish: π × (${fixed(finish / 2, 4)})² × ${length}.` },
          { kind: 'compute', text: `${fixed(before, 3)} − ${fixed(after, 3)}` },
          { kind: 'result', text: `= ${fixed(removed, 3)} cubic inches` },
          {
            kind: 'note',
            text: 'Volume goes with the square of the diameter, so taking a quarter inch off a big bar removes far more than off a small one.',
          },
        ],
        ...mcOptions(rng, `${fixed(removed, 2)} cu in`, [
          {
            label: `${fixed(cylinderVolume(cut, length).value, 2)} cu in`,
            value: cylinderVolume(cut, length).value,
            trap: 'You treated the difference in diameter as if it were its own bar. It is a ring coming off the outside, not a rod.',
          },
          {
            label: `${fixed(cut * length, 2)} cu in`,
            value: cut * length,
            trap: 'No π and no squaring — that is not a volume.',
          },
          {
            label: `${fixed(before, 2)} cu in`,
            value: before,
            trap: 'That is the whole starting bar. Only the difference comes off.',
          },
        ]),
      };
    },
  },
];
